import cookie from "@/utils/cookie";

const decodeCookie = async (cookieName, dispatch) => {
	const jwtPayload = cookie.getCookie(cookieName);
	if (!jwtPayload) {
		await dispatch("destroyChamaileonSpa", { errorCode: "noSessionToken" }, { root: true });
		throw new Error(`Cookie "${cookieName}" not available`);
	}

	const payload = jwtPayload.split(".")[1];
	const decoded = JSON.parse(atob(payload));

	const jwtExpiration = new Date(decoded.exp).getTime() * 1000;
	if (jwtExpiration < Date.now()) {
		await dispatch("destroyChamaileonSpa", { errorCode: "sessionExpired" }, { root: true });
		throw new Error(`Cookie "${cookieName}" expired`);
	}

	return { payload: jwtPayload, decoded };
};

export default function ({ globals }) {
	return {
		namespaced: true,
		state: {
			userToken: null,
			cognitoToken: null,
			cognitoRefreshToken: null,
			refreshErrors: 0,
			lastCheck: null,
		},
		mutations: {
			SET_USER_TOKEN(state, userToken) {
				state.userToken = userToken;
			},
			SET_COGNITO_TOKEN(state, cognitoToken) {
				state.cognitoToken = cognitoToken;
			},
			SET_COGNITO_REFRESH_TOKEN(state, cognitoRefreshToken) {
				state.cognitoRefreshToken = cognitoRefreshToken;
			},

			INCREMENT_REFRESH_ERRORS(state) {
				state.refreshErrors++;
			},
			RESET_ERROR_COUNTER(state) {
				state.refreshErrors = 0;
			},
			SET_LAST_CHECK(state, value) {
				state.lastCheck = value;
			},
		},
		actions: {
			async checkTokens({ state, dispatch, commit }) {
				await dispatch("checkIfTokensAreSetAndIfNotSetThem");

				const currentTime = Math.floor(Date.now() / 1000);
				const minutesToExpire = (state.userToken.expiration - currentTime) / 60;
				const currentCheck = Date.now();

				if (state.lastCheck !== null && currentCheck - state.lastCheck < (60 * 2 * 1000)) {
					return;
				}

				if (state.lastCheck === null) {
					commit("SET_LAST_CHECK", currentCheck);
				}

				if (minutesToExpire < 15) {
					commit("SET_LAST_CHECK", currentCheck);
					await dispatch("refreshToken");
				}
			},
			async checkIfTokensAreSetAndIfNotSetThem({ dispatch, state, commit, rootState }) {
				let envName;

				if (rootState.environment.name === "dev") envName = "dev";
				else if (rootState.environment.name === "prod") envName = "prod";
				else if (rootState.environment.name === "prod-santander") envName = "santander-prod";
				else envName = "staging";

				if (!state.userToken || !state.cognitoToken || !state.cognitoRefreshToken) {
					const { payload, decoded } = await decodeCookie(`ch-user-access-token-${envName}`, dispatch);

					commit("SET_USER_TOKEN", {
						jwt: payload,
						expiration: decoded.exp,
						refreshInterval: 23 * 60 * 60 * 1000,
					});

					const cognitoPayload = cookie.getCookie(`cognito-token-${envName}`);
					commit("SET_COGNITO_TOKEN", { jwt: cognitoPayload });

					const refreshPayload = cookie.getCookie(`cognito-refresh-token-${envName}`);
					commit("SET_COGNITO_REFRESH_TOKEN", { jwt: refreshPayload });
				}
			},
			async getTokens({ state, rootState, dispatch }) {
				let envName;

				if (rootState.environment.name === "dev") envName = "dev";
				else if (rootState.environment.name === "prod") envName = "prod";
				else if (rootState.environment.name === "prod-santander") envName = "santander-prod";
				else envName = "staging";

				await dispatch("checkIfTokensAreSetAndIfNotSetThem");

				return {
					Authorization: `Bearer ${state.userToken.jwt}`,
					[`cognito-token-${envName}`]: state.cognitoToken.jwt,
					[`cognito-refresh-token-${envName}`]: state.cognitoRefreshToken.jwt,
				};
			},
			async refreshToken({ state, commit, dispatch }) {
				try {
					const payload = state.userToken.jwt.split(".")[1];
					const { userId } = JSON.parse(atob(payload));
					const tokens = await dispatch("getTokens");

					const config = await globals.getAsyncGlobalVariable("environmentConfig");
					const response = await fetch(`${config.backends.users}/user/${userId}/access-token`, {
						headers: { "Content-Type": "application/json", ...tokens },
						credentials: "include",
					});

					const { result: { accessToken = {}, cognitoToken = {} } } = await response.json();

					const newPayload = accessToken.token.split(".")[1];
					const decoded = JSON.parse(atob(newPayload));

					dispatch("setChamaileonCookies", { userToken: accessToken, cognitoToken }, { root: true });
					commit("SET_USER_TOKEN", {
						jwt: accessToken.token,
						expiration: decoded.exp,
						refreshInterval: 23 * 60 * 60 * 1000,
					});
					commit("SET_COGNITO_TOKEN", { jwt: cognitoToken.token });
				} catch (err) {
					if (err.message === "Failed to fetch") return;
					commit("INCREMENT_REFRESH_ERRORS");
					if (state.refreshErrors > 4) await dispatch("destroyChamaileonSpa", { errorCode: "refreshError" }, { root: true });
				}
			},
			clearTokens({ commit, rootState }) {
				let envName;
				if (rootState.environment.name === "dev") envName = "dev";
				else if (rootState.environment.name === "prod") envName = "prod";
				else if (rootState.environment.name === "prod-santander") envName = "santander-prod";
				else envName = "staging";

				cookie.removeCookie(`ch-user-access-token-${envName}`);
				cookie.removeCookie(`cognito-token-${envName}`);
				cookie.removeCookie(`cognito-refresh-token-${envName}`);

				commit("SET_USER_TOKEN", null);
				commit("SET_COGNITO_TOKEN", null);
				commit("SET_COGNITO_REFRESH_TOKEN", null);
				commit("SET_LAST_CHECK", null);
				commit("RESET_ERROR_COUNTER", null);
			},
		},
	};
}
